// Tracking settings service for managing tracking enable/disable state
import { pb } from '@/lib/pocketbase';

const SETTINGS_COLLECTION = 'app_settings';
const TRACKING_SETTING_KEY = 'tracking_enabled';

const LOCAL_STORAGE_KEY = 'app:tracking_enabled';

const readLocalTrackingEnabled = (): boolean | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
  if (raw === null) return null;
  return raw === 'true';
};

const writeLocalTrackingEnabled = (enabled: boolean) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LOCAL_STORAGE_KEY, String(enabled));
};

/**
 * Get tracking enabled status
 * Returns true by default if setting doesn't exist
 */
export const getTrackingEnabled = async (): Promise<boolean> => {
  // Privacy-first: if this browser explicitly turned tracking OFF, keep it OFF
  // even if the remote read fails or returns stale data.
  const local = readLocalTrackingEnabled();
  if (local === false) return false;

  try {
    const records = await pb.collection(SETTINGS_COLLECTION).getList(1, 1, {
      filter: `key = "${TRACKING_SETTING_KEY}"`,
      requestKey: `tracking-setting-${Date.now()}`
    });

    if (records.items.length === 0) {
      // Nothing stored server-side → fall back to local → otherwise default true
      return local ?? true;
    }

    const setting = records.items[0];
    const enabled = setting.value === true || setting.value === 'true';

    // Keep local cache in sync for refresh reliability
    writeLocalTrackingEnabled(enabled);

    return enabled;
  } catch (error: any) {
    console.error('Failed to get tracking setting:', error);

    // PocketBase filter is currently returning 400 in some environments.
    // Ensure the toggle still persists on refresh in this browser.
    return local ?? true;
  }
};

/**
 * Set tracking enabled status
 */
export const setTrackingEnabled = async (enabled: boolean): Promise<boolean> => {
  // Always persist locally so refresh keeps the same state even if server read fails
  writeLocalTrackingEnabled(enabled);

  try {
    // Try to find existing setting using getList instead of getFirstListItem
    let existing = null;
    try {
      const records = await pb.collection(SETTINGS_COLLECTION).getList(1, 1, {
        filter: `key = "${TRACKING_SETTING_KEY}"`,
        requestKey: `tracking-setting-find-${Date.now()}`
      });
      if (records.items.length > 0) {
        existing = records.items[0];
      }
    } catch (e) {
      // Collection might not exist or filter might fail
      console.log('No existing tracking setting found');
    }

    if (existing) {
      // Update existing
      await pb.collection(SETTINGS_COLLECTION).update(existing.id, {
        value: enabled
      });
      console.log('Updated tracking setting to:', enabled);
    } else {
      // Create new
      await pb.collection(SETTINGS_COLLECTION).create({
        key: TRACKING_SETTING_KEY,
        value: enabled
      });
      console.log('Created tracking setting:', enabled);
    }

    // Invalidate cache immediately
    invalidateTrackingCache();

    return true;
  } catch (error) {
    console.error('Failed to set tracking setting:', error);
    // Still return true because local persistence succeeded (toggle should stay off after refresh)
    invalidateTrackingCache();
    return true;
  }
};

// Cache the setting in memory for performance
let cachedTrackingEnabled: boolean | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 60000; // 1 minute cache

/**
 * Get tracking enabled status with caching
 * Used by metaPixel to avoid too many PocketBase calls
 */
export const isTrackingEnabled = async (): Promise<boolean> => {
  const now = Date.now();

  // Return cached value if still valid
  if (cachedTrackingEnabled !== null && (now - cacheTimestamp) < CACHE_TTL) {
    return cachedTrackingEnabled;
  }

  // Fetch fresh value
  cachedTrackingEnabled = await getTrackingEnabled();
  cacheTimestamp = now;

  return cachedTrackingEnabled;
};

/**
 * Invalidate the cache (call after updating setting)
 */
export const invalidateTrackingCache = () => {
  cachedTrackingEnabled = null;
  cacheTimestamp = 0;
};
